import React, {useRef, useState, useMemo} from "react";
import {Animated, Platform, StyleSheet, useWindowDimensions, View} from 'react-native';
import ScreenView from "../components/common/ScreenView";
import {useStories} from "../hooks/useStory";
import Emoji from "../components/common/Emoji";
import {RootStackParamList} from "../types";
import Heading from "../components/common/Heading";
import Text from "../components/common/Text";
import Button from "../components/common/Button";
import Carousel from "react-native-snap-carousel";
import SimpleWebCarousel from "../components/home/SimpleWebCarousel";
import {NavigationProp, useNavigation} from "@react-navigation/native";
import Card from "../components/home/Card";
import { getContentMargin, getCarouselTextSpacing, scale, SCREEN_BREAKPOINTS } from "../utils/spacing";

/**
 * Home screen which contains a video choosing carousel.
 */
const HomeScreen: React.VFC = () => {
    const stories = useStories();
    const [ selected, setSelected ] = useState(0);

    const story = stories[selected];

    const { width, height } = useWindowDimensions();

    const animation = useRef(new Animated.Value(0)).current;
    const transition = useRef(Animated.timing(animation,{
        useNativeDriver: false,
        toValue: 1,
        duration: 500
    })).current;

    const background = useRef(animation.interpolate({
        inputRange: [0, 1],
        outputRange: [stories[0].background, stories[0].background]
    }));

    const _set = (item: number) => {
        background.current = animation.interpolate({
            inputRange: [0, 1],
            outputRange: [stories[selected].background, stories[item].background]
        });

        transition.reset();
        transition.start();
        setSelected(item);
    };

    const { navigate } = useNavigation<NavigationProp<RootStackParamList>>();
    const _start = () => {
        navigate("Story", { story: selected, choices: [] });
    };

    const carousel = height * 0.5;

    // Calculate responsive card width with better constraints
    const minCardWidth = Math.min(width * 0.7, 280); // At least 70% of width, max 280px minimum
    const maxCardWidth = Math.min(width - scale(60, width), 500); // Leave room for side padding
    let cardWidth = width - scale(120, width);

    // Maintain 4:3 aspect ratio
    if (carousel / cardWidth < 4 / 3) {
        cardWidth = carousel * 3 / 4;
    }

    // Apply constraints
    cardWidth = Math.max(minCardWidth, Math.min(cardWidth, maxCardWidth));

    // Calculate responsive spacing values
    const contentMargin = useMemo(() => getContentMargin(width), [width]);
    const textSpacing = useMemo(() => getCarouselTextSpacing(width), [width]);

    // Dynamic margin for description - make it responsive instead of hardcoded
    const descriptionMargin = useMemo(() => {
        if (width < SCREEN_BREAKPOINTS.SMALL) {
            return scale(12, width);
        } else if (selected === 1) {
            // Story 1 gets tighter margins for longer text
            return scale(15, width);
        } else {
            return contentMargin;
        }
    }, [width, selected, contentMargin]);

    return (
        <ScreenView header headerColor="white">
            <Animated.View style={[ styles.container, { backgroundColor: background.current }]} >
                <View style={[styles.emojis, { marginBottom: scale(8, width) }]}>
                    {story.emoji.map(e => <Emoji key={e} char={e} size={scale(40, width)} />)}
                </View>
                <Heading style={styles.heading}>{story.title}</Heading>
                <Text style={[
                    styles.description,
                    {
                        marginHorizontal: descriptionMargin,
                        marginBottom: textSpacing,
                        fontSize: scale(14, width),
                        lineHeight: scale(20.3, width)
                    }
                ]} numberOfLines={2}>{story.description}</Text>

                <View style={{ height: carousel, width: '100%' }}>
                    {Platform.OS === 'web' ? (
                        <SimpleWebCarousel
                          data={stories}
                          selected={selected}
                          onSnapToItem={_set}
                          itemWidth={cardWidth}
                          onCardPress={_start}
                        />
                    ) : (
                        <Carousel
                          data={stories}
                          renderItem={({ item, index }: any) => (
                              <Card story={item} selected={index === selected} />
                          )}
                          sliderWidth={width}
                          itemWidth={cardWidth}
                          onSnapToItem={_set}
                        />
                    )}
                </View>

                <View style={[styles.button, { marginTop: textSpacing }]}>
                    <Button label="Get Started" type="secondary" onPress={_start} />
                </View>
            </Animated.View>
        </ScreenView>
    );
};

const styles = StyleSheet.create({
    container: {
        width: "100%",
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: 40
    },
    heading: {
        textTransform: "uppercase"
    },
    description: {
        // Margins and font sizes are now applied inline for responsive behavior
        textAlign: 'center' as 'center',
    },
    emojis: {
        flexDirection: "row",
        justifyContent: "center"
    },
    button: {
        width: 220
    },
});

export default HomeScreen;